import { useState } from 'react';
import { Save, Share2, Trash2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import QRShareModal from '@/components/QRShareModal';
import { chordName, droneKey } from '@/lib/droneChords';
import { applySong, buildSongShareUrl, parseSong, songFromDrone, useSavedSongs } from '@/lib/droneSongs';

const inputCls = 'h-9 rounded-lg bg-[#0A0A0A] border border-white/10 px-2 text-sm text-white focus:outline-none focus:border-sky-400';
const btn = 'h-9 px-3 shrink-0 rounded-lg border text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 disabled:opacity-30 active:scale-95 transition-transform';

// Save / recall / share the whole timed drone (chords, key, timbre, BPM).
export const DroneSongs = ({ drone, flats }) => {
  const { list, save, remove } = useSavedSongs();
  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [sharing, setSharing] = useState(null);

  const load = (id) => {
    setSelected(id);
    const item = list.find((x) => x.id === id);
    const song = item && parseSong(item.song);
    if (!song) return;
    applySong(drone, song);
    setName(item.name);
    toast.success(`Sequenza "${item.name}" caricata`);
  };
  const onSave = () => {
    const id = save(name, songFromDrone(drone));
    setSelected(id);
    toast.success(`Sequenza "${name.trim()}" salvata`);
  };
  const share = () => {
    const song = songFromDrone(drone);
    const chords = drone.prog.steps.map((s) => `${chordName(s.pc, s.quality, flats)}${s.bars > 1 ? `×${s.bars}` : ''}`).join(' – ');
    setSharing({ url: buildSongShareUrl(song, name.trim()), title: name.trim() || 'Bordone a tempo', subtitle: `${chords} · ${song.bpm} BPM · strumento in ${droneKey(song.k).label}` });
  };

  return (
    <div data-testid="drone-songs" className="flex flex-col gap-2 border-t border-white/5 pt-3">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Sequenze salvate</span>
      <div className="flex gap-2">
        <select data-testid="drone-songs-select" aria-label="Sequenze salvate" value={selected} onChange={(e) => load(e.target.value)} className={`${inputCls} flex-1 min-w-0`}>
          <option value="" disabled>{list.length ? 'Richiama una sequenza…' : 'Nessuna sequenza salvata'}</option>
          {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <button data-testid="drone-songs-delete" aria-label="Elimina sequenza" disabled={!selected} onClick={() => { remove(selected); setSelected(''); }} className={`${btn} border-white/10 text-white/60 w-9 justify-center px-0`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex gap-2">
        <input data-testid="drone-songs-name" value={name} maxLength={40} placeholder="Nome sequenza" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onSave(); }} className={`${inputCls} flex-1 min-w-0`} />
        <button data-testid="drone-songs-save" disabled={!name.trim()} onClick={onSave} className={`${btn} border-pink-400/40 text-pink-300`}>
          <Save className="w-3.5 h-3.5" /> Salva
        </button>
        <button data-testid="drone-song-share" onClick={share} className={`${btn} border-sky-400/40 text-sky-300`}>
          <Share2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Condividi</span>
        </button>
      </div>
      <QRShareModal
        open={!!sharing}
        url={sharing?.url || ''}
        title={sharing?.title || ''}
        subtitle={sharing?.subtitle || ''}
        caption="Scansiona per aprire la sequenza di accordi"
        onClose={() => setSharing(null)}
      />
    </div>
  );
};
